import { useGetByIdDashboardQuery, useUpdateDashboardMutation } from "@/redux/api/dashboardApi";
import { setDashboardFilter, setGroupData } from "@/redux/slices/dashboardV2.slice";
import { App, Button, Form, Modal, Spin, Tabs } from "antd";
import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { MdOutlineDashboardCustomize } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { CustomSelectorTab } from "./CustomSelectorTab";
import LiveEditTab from "./LiveEditTab";
import TimeSelectorTab from "./TimeSelectorTab";
import DashboardPreview from "./DashboardPreview";

const onFinishFailed = (errorInfo) => {
  // eslint-disable-next-line no-console
  console.log("Failed:", errorInfo);
};

const EMPTY_OBJECT = {};

const CustomSelectorContainer = (props) => {
  const { form, setFormData, gridData, dashboard: dashboardProp, tabId } = props;
  const { notification } = App.useApp();
  const dispatch = useDispatch();
  const { dashboardFilter = {} } = useSelector((state) => state.dashboardV2);

  // state
  const [selectedTab, setSelectedTab] = useState("custom");
  const [show, setShow] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);

  // rkt query
  const [updateDashboard, { isLoading: updateLoading }] = useUpdateDashboardMutation();
  const { data, isFetching: isFetchingDashboard } = useGetByIdDashboardQuery({ id: dashboardProp?._id }, { skip: !dashboardProp?._id });
  const dashboard = data?.data || dashboardProp || EMPTY_OBJECT;

  const prevDashboardIdRef = useRef();
  const prevTabIdRef = useRef();

  // effects
  useEffect(() => {
    if (!dashboard?._id) return;

    const nextData = { ...dashboardFilter };
    if (dashboard.hasTabs) {
      const activeTab = dashboard.tabs?.find((tab) => tab._id === tabId) || {};
      nextData.liveEdit = activeTab.liveEdit || false;
      nextData.groups = activeTab.groups || [];
    } else {
      nextData.liveEdit = dashboard?.tabs?.[0]?.liveEdit || false;
      nextData.groups = dashboard?.tabs?.[0]?.groups || [];
    }

    setFormData((prev) => {
      const isLiveEditSame = prev?.liveEdit === nextData.liveEdit;
      const isGroupsSame = JSON.stringify(prev?.groups || []) === JSON.stringify(nextData.groups || []);
      const isFilterSame = Object.keys(dashboardFilter).every((k) => prev?.[k] === dashboardFilter[k]);

      if (isLiveEditSame && isGroupsSame && isFilterSame) {
        return prev;
      }
      return { ...prev, ...nextData };
    });
    if (show && form) {
      form.setFieldsValue(nextData);
    }
  }, [dashboard, tabId, show, dashboardFilter, form, setFormData]);

  useEffect(() => {
    if (!dashboard?._id) return;

    const hadPreviousDashboard = Boolean(prevDashboardIdRef.current);
    const dashboardChanged = hadPreviousDashboard && prevDashboardIdRef.current !== dashboard._id;
    const tabChanged = hadPreviousDashboard && prevTabIdRef.current && prevTabIdRef.current !== tabId;

    if (dashboardChanged || tabChanged) {
      clearFields();
    }

    prevDashboardIdRef.current = dashboard._id;
    prevTabIdRef.current = tabId;
  }, [dashboard?._id, tabId]);

  const handleUpdate = async (data) => {
    if (!dashboard?._id || !Object.keys(data || {})?.length) return;
    updateDashboard({ id: dashboard._id, body: data });
  };

  // props
  const selectorProps = { ...props, setShow, show };

  const handleClose = () => {
    setShow(false);
    setSelectedTab("custom");
  };

  const handleOpenModal = () => {
    setShow(true);
    setHasOpened(true);
  };

  // submit

  const onFinish = (values) => {
    const { liveEdit, groups } = values;
    setShow(false);
    let body = {};
    if (dashboard.hasTabs) {
      const tabs = dashboard.tabs.map((tab) => (tab._id === tabId ? { ...tab, liveEdit: Boolean(liveEdit), groups: liveEdit ? groups : [] } : tab));
      body.tabs = tabs;
      //
    } else {
      body.tabs[0] = { ...body.tabs[0], liveEdit: Boolean(liveEdit), groups: liveEdit ? groups : [] };
    }

    handleUpdate(body);
    notification.success({ message: `Settings saved successfully.`, duration: 2, showProgress: true });
  };

  const clearFields = () => {
    try {
      const currentBusiness = form?.getFieldValue ? form.getFieldValue(["locations", "businesses"]) : undefined;
      if (form?.resetFields) form.resetFields();
      setFormData((prev) => ({
        ...prev,
        comparisonPeriods: [{ color: "#e81416" }],
      }));
      if (form?.setFieldsValue) {
        form.setFieldsValue({
          comparisonPeriods: [{ date: null, color: "#e81416" }],
          locations: { businesses: currentBusiness },
        });
      }
    } catch {
      // In case form instance is not ready yet
    }

    dispatch(setDashboardFilter({}));
    dispatch(setGroupData([]));
  };

  const restField = () => {
    clearFields();

    // live edit reset
    let body = {};
    if (dashboard.hasTabs) {
      const tabs = dashboard.tabs.map((tab) => (tab._id === tabId ? { ...tab, liveEdit: false, groups: [] } : tab));
      body.tabs = tabs;
    } else {
      body = { liveEdit: false, groups: [] };
    }
    handleUpdate(body);
    // setShow(false);
    // notification.success({ message: `Settings reset successfully.`, duration: 2, showProgress: true});
  };

  const loading = updateLoading || isFetchingDashboard;

  return (
    <>
      <Button onClick={handleOpenModal} className="bg_light_gray" htmlType="button" type="primary" title="Custom Selector">
        <MdOutlineDashboardCustomize />
      </Button>
      <Modal open={show} onCancel={handleClose} footer={null} style={{ minWidth: "50vw" }}>
        <Spin spinning={loading}>
          <div className="preview mb-2" style={{ minHeight: 200 }}>
          <h5>Preview</h5>
          <Row md={4} sm={2} className="g-3">
            {Array.isArray(gridData) &&
              gridData
                ?.filter((data) => dashboardFilter?.selectedTiles?.includes(data?._id))
                ?.map((item, ind) => (
                  <Col key={ind}>
                    <div className="border overflow-hidden" style={{ aspectRatio: "1 / 1" }}>
                      <DashboardPreview item={item} />
                    </div>
                  </Col>
                ))}
          </Row>
        </div>

        <Form
          layout="vertical"
          name="comparisonForm"
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          form={form}
          scrollToFirstError={true}
          onValuesChange={(_, values) => {
            const modified = { ...values };
            if (modified.intervalTime && modified.intervalValue) modified.interval = (modified.intervalTime || 1) * (modified.intervalValue || 1);
            dispatch(setDashboardFilter(modified));
          }}
          initialValues={{ comparisonPeriods: [{ color: "#e81416" }] }}
        >
          <Tabs
            defaultActiveKey="custom"
            activeKey={selectedTab}
            onChange={(key) => setSelectedTab(key)}
            items={[
              {
                key: "custom",
                label: "Custom Selector",
                children: <CustomSelectorTab {...selectorProps} />,
              },
              {
                key: "time",
                label: "Time Selector",
                children: <TimeSelectorTab {...selectorProps} />,
              },
              {
                key: "LiveEdit",
                label: "Live Edit",
                children: <LiveEditTab {...selectorProps} />,
              },
            ]}
          />

          <div className="text-end mt-4">
            <Button type="primary" className="me-2" danger onClick={restField} htmlType="button">
              Reset
            </Button>
            <Button type="primary" htmlType="submit">
              Set
            </Button>
          </div>
        </Form>
        </Spin>
      </Modal>
    </>
  );
};

export default CustomSelectorContainer;

CustomSelectorContainer.propTypes = {
  form: PropTypes.object.isRequired,
  setFormData: PropTypes.func.isRequired,
  gridData: PropTypes.array,
  selectedTiles: PropTypes.array,
  dashboard: PropTypes.object,
  tabId: PropTypes.string,
};
